import { apiFetch } from "../../lib/api";
import { useState, useEffect, useRef, useCallback } from "react";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ReplayEventType =
  | "PromptSent"
  | "TokenStreamed"
  | "ToolCall"
  | "ToolResult"
  | "Decision"
  | "Error"
  | "Done";

interface ReplayEvent {
  id: string;
  timestamp: string;
  event_type: ReplayEventType;
  provider: string;
  model: string;
  data: unknown;
}

type SessionStatus = "Recording" | "Completed" | "Failed";

interface ReplaySessionMeta {
  session_id: string;
  started_at: string;
  prompt: string;
  provider: string;
  status: SessionStatus;
  event_count: number;
}

interface ReplaySessionFull extends ReplaySessionMeta {
  events: ReplayEvent[];
}

/* ------------------------------------------------------------------ */
/*  Design tokens                                                      */
/* ------------------------------------------------------------------ */

const BG = "#0d0d0d";
const BG_PANEL = "rgba(9,11,14,0.94)";
const BORDER = "rgba(255,255,255,0.06)";
const ACCENT = "#4f8fff";
const TEXT = "#f8f8f2";
const TEXT_DIM = "#a8b2b8";
const MONO = "'JetBrains Mono', 'Fira Code', monospace";

const EVENT_COLORS: Record<ReplayEventType, string> = {
  PromptSent: "#4f8fff",
  TokenStreamed: "#8be9fd",
  ToolCall: "#bd93f9",
  ToolResult: "#fbbf24",
  Decision: "#ffb86c",
  Error: "#ff5555",
  Done: "#4fd6be",
};

const STATUS_COLORS: Record<SessionStatus, string> = {
  Recording: "#ffb86c",
  Completed: "#4f8fff",
  Failed: "#ff5555",
};

/* ------------------------------------------------------------------ */
/*  Demo data (used when no live API is available)                     */
/* ------------------------------------------------------------------ */

function demoBuildSessions(): ReplaySessionMeta[] {
  const now = new Date();
  return [
    {
      session_id: "replay-1",
      started_at: new Date(now.getTime() - 180_000).toISOString(),
      prompt: "Refactor the auth middleware to use JWT tokens",
      provider: "claude",
      status: "Completed",
      event_count: 14,
    },
    {
      session_id: "replay-2",
      started_at: new Date(now.getTime() - 60_000).toISOString(),
      prompt: "Add pagination to the /api/projects endpoint",
      provider: "codex",
      status: "Recording",
      event_count: 7,
    },
    {
      session_id: "replay-3",
      started_at: new Date(now.getTime() - 3600_000).toISOString(),
      prompt: "Fix CORS headers for browser requests",
      provider: "ollama",
      status: "Failed",
      event_count: 4,
    },
  ];
}

function demoBuildEvents(sessionId: string): ReplayEvent[] {
  const base = Date.now() - 120_000;
  const types: ReplayEventType[] = [
    "PromptSent",
    "TokenStreamed",
    "TokenStreamed",
    "ToolCall",
    "ToolResult",
    "Decision",
    "TokenStreamed",
    "ToolCall",
    "ToolResult",
    "TokenStreamed",
    "TokenStreamed",
    "Decision",
    "TokenStreamed",
    "Done",
  ];
  return types.map((t, i) => ({
    id: `${sessionId}-evt-${i}`,
    timestamp: new Date(base + i * 2400).toISOString(),
    event_type: t,
    provider: "claude",
    model: "opus-4",
    data:
      t === "PromptSent"
        ? { prompt: "Refactor the auth middleware" }
        : t === "TokenStreamed"
          ? { token: "function " + ["verify", "decode", "sign", "wrap", "emit"][i % 5] }
          : t === "ToolCall"
            ? { name: "read_file", input: { path: "src/middleware/auth.rs" } }
            : t === "ToolResult"
              ? { name: "read_file", output: "pub fn verify_token(...) { ... }" }
              : t === "Decision"
                ? { action: "apply_edit", reasoning: "Token verification needs HMAC check" }
                : t === "Error"
                  ? { message: "Provider timeout after 30s" }
                  : { thread_id: sessionId },
  }));
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max) + "\u2026" : s;
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatusDot({ status }: { status: SessionStatus }) {
  const color = STATUS_COLORS[status];
  const pulse = status === "Recording";
  return (
    <span
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: "50%",
        background: color,
        boxShadow: pulse ? `0 0 8px ${color}` : undefined,
        animation: pulse ? "replay-pulse 1.4s ease-in-out infinite" : undefined,
        flexShrink: 0,
      }}
    />
  );
}

function EventBadge({ type }: { type: ReplayEventType }) {
  const color = EVENT_COLORS[type];
  const labels: Record<ReplayEventType, string> = {
    PromptSent: "PROMPT",
    TokenStreamed: "TOKEN",
    ToolCall: "TOOL",
    ToolResult: "RESULT",
    Decision: "DECIDE",
    Error: "ERROR",
    Done: "DONE",
  };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        borderRadius: 4,
        background: `${color}18`,
        border: `1px solid ${color}40`,
        color,
        font: `700 9px/1 ${MONO}`,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        whiteSpace: "nowrap",
      }}
    >
      {labels[type]}
    </span>
  );
}

function PlaybackBar({
  playing,
  onPlay,
  onPause,
  onStepBack,
  onStepForward,
  speed,
  onSpeedChange,
  current,
  total,
}: {
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStepBack: () => void;
  onStepForward: () => void;
  speed: number;
  onSpeedChange: (s: number) => void;
  current: number;
  total: number;
}) {
  const btnStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.04)",
    border: `1px solid ${BORDER}`,
    borderRadius: 6,
    color: TEXT,
    padding: "6px 12px",
    font: `700 11px/1 ${MONO}`,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
  };
  const speeds = [0.5, 1, 2, 4];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 16px",
        borderTop: `1px solid ${BORDER}`,
        background: "rgba(0,0,0,0.3)",
      }}
    >
      <button type="button" style={btnStyle} onClick={onStepBack} title="Step back">
        {"<"}
      </button>
      {playing ? (
        <button type="button" style={{ ...btnStyle, background: `${ACCENT}18`, borderColor: `${ACCENT}40` }} onClick={onPause} title="Pause">
          ||
        </button>
      ) : (
        <button type="button" style={{ ...btnStyle, background: `${ACCENT}18`, borderColor: `${ACCENT}40` }} onClick={onPlay} title="Play">
          {"\u25B6"}
        </button>
      )}
      <button type="button" style={btnStyle} onClick={onStepForward} title="Step forward">
        {">"}
      </button>

      <div style={{ flex: 1 }} />

      <span style={{ font: `400 11px/1 ${MONO}`, color: TEXT_DIM }}>
        {current + 1}/{total}
      </span>

      <div style={{ display: "flex", gap: 4 }}>
        {speeds.map((s) => (
          <button
            key={s}
            type="button"
            style={{
              ...btnStyle,
              padding: "4px 8px",
              fontSize: 10,
              background: speed === s ? `${ACCENT}20` : undefined,
              borderColor: speed === s ? `${ACCENT}50` : BORDER,
              color: speed === s ? ACCENT : TEXT_DIM,
            }}
            onClick={() => onSpeedChange(s)}
          >
            {s}x
          </button>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export function ReplayWorkbench() {
  const [sessions, setSessions] = useState<ReplaySessionMeta[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [events, setEvents] = useState<ReplayEvent[]>([]);
  const [cursor, setCursor] = useState(0);
  const [detailEvent, setDetailEvent] = useState<ReplayEvent | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Load sessions on mount (falls back to demo data).
  useEffect(() => {
    apiFetch("/api/replay/sessions")
      .then((r) => r.json())
      .then((data: ReplaySessionMeta[]) => {
        if (data.length) setSessions(data);
        else setSessions(demoBuildSessions());
      })
      .catch(() => setSessions(demoBuildSessions()));
  }, []);

  // Load events when a session is selected.
  useEffect(() => {
    if (!selectedId) {
      setEvents([]);
      return;
    }
    fetch(`/api/replay/sessions/${selectedId}`)
      .then((r) => r.json())
      .then((data: ReplaySessionFull) => {
        if (data.events?.length) setEvents(data.events);
        else setEvents(demoBuildEvents(selectedId));
      })
      .catch(() => setEvents(demoBuildEvents(selectedId)));

    setCursor(0);
    setDetailEvent(null);
    setPlaying(false);
  }, [selectedId]);

  // Playback timer.
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (!playing || events.length === 0) return;

    intervalRef.current = setInterval(() => {
      setCursor((c) => {
        const next = c + 1;
        if (next >= events.length) {
          setPlaying(false);
          return c;
        }
        return next;
      });
    }, 600 / speed);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing, speed, events.length]);

  // Auto-scroll timeline to cursor.
  useEffect(() => {
    if (!timelineRef.current) return;
    const el = timelineRef.current.children[cursor] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [cursor]);

  const selectSession = useCallback((id: string) => {
    setSelectedId(id);
  }, []);

  const stepForward = useCallback(() => {
    setCursor((c) => Math.min(c + 1, events.length - 1));
  }, [events.length]);

  const stepBack = useCallback(() => {
    setCursor((c) => Math.max(c - 1, 0));
  }, []);

  const visibleEvents = events.slice(0, cursor + 1);

  return (
    <main
      className="ui-shell"
      style={{
        background: BG,
        minHeight: "100vh",
        padding: 0,
        display: "grid",
        gridTemplateColumns: "320px 1fr",
        gridTemplateRows: "auto 1fr",
        gap: 0,
      }}
    >
      {/* Keyframes */}
      <style>{`
        @keyframes replay-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes replay-fade-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ---- Header ---- */}
      <header
        style={{
          gridColumn: "1 / -1",
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "16px 24px",
          borderBottom: `1px solid ${BORDER}`,
          background: BG_PANEL,
        }}
      >
        <span
          style={{
            color: ACCENT,
            font: `700 10px/1 ${MONO}`,
            textTransform: "uppercase",
            letterSpacing: "0.14em",
          }}
        >
          Replay Debugger
        </span>
        <span style={{ color: TEXT_DIM, fontSize: 12 }}>
          Time-travel through agent decisions
        </span>
      </header>

      {/* ---- Session list (left) ---- */}
      <aside
        style={{
          borderRight: `1px solid ${BORDER}`,
          background: BG_PANEL,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "12px 16px 8px",
            font: `700 10px/1 ${MONO}`,
            textTransform: "uppercase",
            letterSpacing: "0.12em",
            color: TEXT_DIM,
          }}
        >
          Sessions ({sessions.length})
        </div>

        {sessions.map((s) => {
          const active = s.session_id === selectedId;
          return (
            <button
              key={s.session_id}
              type="button"
              onClick={() => selectSession(s.session_id)}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                padding: "12px 16px",
                border: "none",
                borderLeft: active ? `3px solid ${ACCENT}` : "3px solid transparent",
                background: active ? "rgba(141,241,189,0.06)" : "transparent",
                cursor: "pointer",
                textAlign: "left",
                borderBottom: `1px solid ${BORDER}`,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                if (!active) (e.currentTarget as HTMLElement).style.background = "rgba(255,255,255,0.03)";
              }}
              onMouseLeave={(e) => {
                if (!active) (e.currentTarget as HTMLElement).style.background = "transparent";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <StatusDot status={s.status} />
                <span style={{ font: `700 11px/1 ${MONO}`, color: TEXT, flex: 1 }}>
                  {s.provider}
                </span>
                <span style={{ font: `400 10px/1 ${MONO}`, color: TEXT_DIM }}>
                  {formatRelative(s.started_at)}
                </span>
              </div>
              <div style={{ color: TEXT_DIM, fontSize: 12, lineHeight: 1.4 }}>
                {truncate(s.prompt, 80)}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span
                  style={{
                    font: `700 9px/1 ${MONO}`,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                    color: STATUS_COLORS[s.status],
                  }}
                >
                  {s.status}
                </span>
                <span style={{ font: `400 10px/1 ${MONO}`, color: TEXT_DIM }}>
                  {s.event_count} events
                </span>
              </div>
            </button>
          );
        })}

        {sessions.length === 0 && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "32px 16px", gap: 10, textAlign: "center" }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(79,143,255,0.08)", border: `1px solid ${BORDER}` }}>
              <svg viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: 22, height: 22, opacity: 0.7 }}>
                <path d="M3 12a9 9 0 109-9 M3 3v9h9" />
              </svg>
            </div>
            <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, fontFamily: MONO }}>No recorded sessions</div>
            <div style={{ fontSize: 11, color: TEXT_DIM, lineHeight: 1.5, maxWidth: 200 }}>
              Sessions are recorded automatically when agents process prompts. Start a chat to generate your first replay.
            </div>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("opentool-open-app", { detail: { id: "chat" } }))}
              style={{ marginTop: 4, padding: "4px 12px", borderRadius: 6, border: `1px solid ${ACCENT}`, background: "rgba(79,143,255,0.08)", color: ACCENT, fontSize: 11, fontWeight: 500, fontFamily: MONO, cursor: "pointer" }}
            >
              Open Chat
            </button>
            <div style={{ fontSize: 10, color: TEXT_DIM, marginTop: 2 }}>
              <kbd style={{ background: BG_PANEL, border: `1px solid ${BORDER}`, borderRadius: 3, padding: "0 4px", fontFamily: MONO, fontSize: 10, color: TEXT_DIM }}>Ctrl+R</kbd>{" "}Replay
            </div>
          </div>
        )}
      </aside>

      {/* ---- Timeline + detail (right) ---- */}
      <section
        style={{
          display: "grid",
          gridTemplateRows: selectedId ? "1fr auto" : "1fr",
          minHeight: 0,
          background: BG,
        }}
      >
        {selectedId && events.length > 0 ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: detailEvent ? "1fr 380px" : "1fr",
                minHeight: 0,
              }}
            >
              {/* Timeline */}
              <div
                ref={timelineRef}
                style={{
                  overflowY: "auto",
                  padding: "16px 24px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                }}
              >
                {visibleEvents.map((evt, idx) => {
                  const isActive = idx === cursor;
                  const isSelected = detailEvent?.id === evt.id;
                  return (
                    <div
                      key={evt.id}
                      onClick={() => {
                        setCursor(idx);
                        setDetailEvent(evt);
                      }}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "72px 20px 1fr",
                        gap: 12,
                        padding: "10px 0",
                        cursor: "pointer",
                        background: isSelected
                          ? "rgba(141,241,189,0.05)"
                          : isActive
                            ? "rgba(255,255,255,0.02)"
                            : "transparent",
                        borderRadius: 8,
                        paddingLeft: 12,
                        paddingRight: 12,
                        animation: "replay-fade-in 0.2s ease-out",
                        transition: "background 0.15s",
                      }}
                    >
                      {/* Timestamp */}
                      <span
                        style={{
                          font: `400 11px/1.6 ${MONO}`,
                          color: isActive ? TEXT : TEXT_DIM,
                          whiteSpace: "nowrap",
                          paddingTop: 2,
                        }}
                      >
                        {formatTime(evt.timestamp)}
                      </span>

                      {/* Timeline rail */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        <div
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            background: EVENT_COLORS[evt.event_type],
                            boxShadow: isActive
                              ? `0 0 10px ${EVENT_COLORS[evt.event_type]}60`
                              : undefined,
                            flexShrink: 0,
                            marginTop: 4,
                          }}
                        />
                        {idx < visibleEvents.length - 1 && (
                          <div
                            style={{
                              width: 2,
                              flex: 1,
                              background: "rgba(255,255,255,0.08)",
                              marginTop: 4,
                            }}
                          />
                        )}
                      </div>

                      {/* Event body */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <EventBadge type={evt.event_type} />
                          <span
                            style={{
                              font: `400 10px/1 ${MONO}`,
                              color: TEXT_DIM,
                            }}
                          >
                            {evt.provider} / {evt.model}
                          </span>
                        </div>
                        <div
                          style={{
                            font: `400 12px/1.5 ${MONO}`,
                            color: TEXT,
                            opacity: 0.8,
                            maxHeight: 40,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {typeof evt.data === "object"
                            ? truncate(JSON.stringify(evt.data), 120)
                            : String(evt.data)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Event detail panel */}
              {detailEvent && (
                <aside
                  style={{
                    borderLeft: `1px solid ${BORDER}`,
                    background: BG_PANEL,
                    overflowY: "auto",
                    padding: 20,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        font: `700 10px/1 ${MONO}`,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: TEXT_DIM,
                      }}
                    >
                      Event Detail
                    </span>
                    <button
                      type="button"
                      onClick={() => setDetailEvent(null)}
                      style={{
                        background: "none",
                        border: "none",
                        color: TEXT_DIM,
                        cursor: "pointer",
                        font: `400 16px/1 ${MONO}`,
                      }}
                    >
                      x
                    </button>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <EventBadge type={detailEvent.event_type} />
                      <span style={{ font: `400 11px/1 ${MONO}`, color: TEXT }}>
                        {detailEvent.id}
                      </span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: "6px 12px" }}>
                      <span style={{ font: `700 10px/1 ${MONO}`, color: TEXT_DIM }}>time</span>
                      <span style={{ font: `400 11px/1 ${MONO}`, color: TEXT }}>
                        {formatTime(detailEvent.timestamp)}
                      </span>
                      <span style={{ font: `700 10px/1 ${MONO}`, color: TEXT_DIM }}>provider</span>
                      <span style={{ font: `400 11px/1 ${MONO}`, color: TEXT }}>
                        {detailEvent.provider}
                      </span>
                      <span style={{ font: `700 10px/1 ${MONO}`, color: TEXT_DIM }}>model</span>
                      <span style={{ font: `400 11px/1 ${MONO}`, color: TEXT }}>
                        {detailEvent.model}
                      </span>
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        font: `700 10px/1 ${MONO}`,
                        textTransform: "uppercase",
                        letterSpacing: "0.12em",
                        color: TEXT_DIM,
                        marginBottom: 8,
                      }}
                    >
                      Data
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        padding: 12,
                        borderRadius: 8,
                        background: "rgba(0,0,0,0.4)",
                        border: `1px solid ${BORDER}`,
                        color: ACCENT,
                        font: `400 11px/1.6 ${MONO}`,
                        overflowX: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                      }}
                    >
                      {JSON.stringify(detailEvent.data, null, 2)}
                    </pre>
                  </div>
                </aside>
              )}
            </div>

            {/* Playback controls */}
            <PlaybackBar
              playing={playing}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onStepBack={stepBack}
              onStepForward={stepForward}
              speed={speed}
              onSpeedChange={setSpeed}
              current={cursor}
              total={events.length}
            />
          </>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              color: TEXT_DIM,
              padding: 48,
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: 16,
                background: `${ACCENT}10`,
                border: `1px solid ${ACCENT}30`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                font: `700 24px/1 ${MONO}`,
                color: ACCENT,
              }}
            >
              {"\u25B6"}
            </div>
            <span style={{ font: `700 11px/1 ${MONO}`, textTransform: "uppercase", letterSpacing: "0.12em" }}>
              Select a session to begin replay
            </span>
            <span style={{ fontSize: 12, maxWidth: 360, textAlign: "center", lineHeight: 1.6 }}>
              Pick a recorded agent session from the left panel to step through every prompt, tool
              call, and decision the agent made.
            </span>
          </div>
        )}
      </section>
    </main>
  );
}
